import type {
  SelectItem,
  SelectOption,
  SelectOptionsGroup,
  SelectOptionWithNodeLabel,
  SelectOptionWithTextLabel,
} from "@components/select/Select"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type OptionsSegment = { options: SelectOption[]; type: "options" }
export type GroupSegment = {
  disabled?: boolean
  label: string
  options: SelectOption[]
  type: "group"
}
export type Segment = GroupSegment | OptionsSegment

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function getDataAttributes(
  option: SelectOption
): Record<string, boolean | number | string> {
  const attrs: Record<string, boolean | number | string> = {}
  for (const [key, value] of Object.entries(option)) {
    if (key.startsWith("data-") && value !== undefined) {
      attrs[key] = value
    }
  }
  return attrs
}

export function isGroup(item: SelectItem): item is SelectOptionsGroup {
  return "options" in item
}

// ---------------------------------------------------------------------------
// normalizeOptions
// ---------------------------------------------------------------------------
// Transforms a mixed SelectItem[] into a flat list of segments.
// Consecutive ungrouped options are collected into a single "options" segment.
// Groups become their own segment with disabled inherited to child options.
// ---------------------------------------------------------------------------
export function normalizeOptions(items: SelectItem[]): Segment[] {
  const segments: Segment[] = []

  for (const item of items) {
    if (isGroup(item)) {
      const options = item.options.map((opt) =>
        item.disabled && !opt.divider ? { ...opt, disabled: true } : opt
      )
      segments.push({
        disabled: item.disabled,
        label: item.label,
        options,
        type: "group",
      })
    } else {
      const lastSegment = segments[segments.length - 1]
      if (lastSegment?.type === "options") {
        lastSegment.options.push(item)
      } else {
        segments.push({ options: [item], type: "options" })
      }
    }
  }

  return segments
}

export function getSelectableOptionsFromSegments(
  segments: Segment[]
): Array<SelectOptionWithNodeLabel | SelectOptionWithTextLabel> {
  return segments
    .flatMap((segment) => segment.options)
    .filter(
      (
        option
      ): option is SelectOptionWithNodeLabel | SelectOptionWithTextLabel =>
        !option.divider
    )
}

// ---------------------------------------------------------------------------
// getIndexByLetter
// ---------------------------------------------------------------------------
// Type-ahead search for combobox options. Finds the index of the first option
// whose text starts with the search string, starting from `startIndex` and
// wrapping around. If all characters in the search string are identical, cycles
// through options starting with that character. Skips disabled options.
//
// Returns `-1` if no match is found.
// ---------------------------------------------------------------------------
export function getIndexByLetter(
  options: SelectOption[],
  searchString: string,
  startIndex: number = 0
): number {
  // Detect repeated same-character input, e.g. "aa" or "bbb".
  // This happens when the user presses the same key multiple times quickly,
  // meaning they want to cycle through options starting with that letter
  // rather than search for a literal "aa" string.
  const allSameChar =
    searchString.length > 1 &&
    searchString.split("").every((ch) => ch === searchString[0])

  if (allSameChar) {
    // Cycle through matches starting AFTER the current option so each
    // repeated keypress advances to the next match instead of staying put.
    //
    // Example: Given options with labels Apple, Apricot, and Banana, startIndex = 0 (Apple)
    //   cycleStart = 1
    //   wrappedOptions contains Apricot, Banana, Apple (starting from index 1)
    //   Finds Apricot at wrappedOptions[0] → original index = (0 + 1) % 3 = 1
    const cycleStart = (startIndex + 1) % options.length
    const wrappedOptions = [
      ...options.slice(cycleStart),
      ...options.slice(0, cycleStart),
    ]
    const lowerChar = searchString[0].toLowerCase()
    const matchIndex = wrappedOptions.findIndex((option) => {
      if (option.disabled) return false
      const text = option.textValue ?? (option.label as string)
      return text.toLowerCase().startsWith(lowerChar)
    })

    // Convert the index in the rotated array back to the original array index.
    //
    // Example: matchIndex = 0, cycleStart = 1
    //   (0 + 1) % 3 = 1 → returns index 1 (Apricot)
    return matchIndex >= 0 ? (matchIndex + cycleStart) % options.length : -1
  }

  // For single characters or multi-character strings (e.g. "ap", "apr"),
  // always search from the beginning of the list and return the first match.
  //
  // Example: Given options with labels Apple, Apricot, and Banana, searchString = "apr"
  //   "Apple".startsWith("apr") → false
  //   "Apricot".startsWith("apr") → true → returns index 1
  const lowerSearch = searchString.toLowerCase()
  const matchIndex = options.findIndex((option) => {
    if (option.disabled) return false
    const text = option.textValue ?? (option.label as string)
    return text.toLowerCase().startsWith(lowerSearch)
  })
  return matchIndex >= 0 ? matchIndex : -1
}
